import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IRecipientList } from '../interfaces/IRecipientList';
import { IDriverLoad } from '../../../../../../../api/loadBoard/interfaces';

interface ICustomerInvoiceState {
    recipients: IRecipientList;
    isBOLRequired: boolean;
    isScaleTicketRequired: boolean;
    isOtherRequired: boolean;
    isQuickbooksRequired: boolean;
}

interface IInvoiceState {
    invoiceLoading: boolean;
    singleInvoiceLoading: boolean;
    multipleInvoiceLoading: boolean;
    recipients: IRecipientList;
    filteredDriverLoads: IDriverLoad[];
    isBOLRequired: boolean;
    isScaleTicketRequired: boolean;
    isOtherRequired: boolean;
    isQuickbooksRequired: boolean;
    customerStates: Record<string, ICustomerInvoiceState>;
    selectedCustomers: number[];
}

const initialCustomerState: ICustomerInvoiceState = {
    recipients: {
        associateId: 0,
        contactIds: [],
        additionalRecipients: [],
    },
    isBOLRequired: false,
    isScaleTicketRequired: false,
    isOtherRequired: false,
    isQuickbooksRequired: false,
};

const initialState: IInvoiceState = {
    invoiceLoading: false,
    singleInvoiceLoading: false,
    multipleInvoiceLoading: false,
    recipients: {
        associateId: 0,
        contactIds: [],
        additionalRecipients: [],
    },
    filteredDriverLoads: [],
    isBOLRequired: false,
    isScaleTicketRequired: false,
    isOtherRequired: false,
    isQuickbooksRequired: false,
    customerStates: {},
    selectedCustomers: [],
};

export const invoiceModal = createSlice({
    name: 'invoiceModal',
    initialState: initialState,
    reducers: {
        setInvoiceLoading: (state, action: PayloadAction<boolean>) => {
            state.invoiceLoading = action.payload;
        },
        setSingleInvoiceLoading: (state, action: PayloadAction<boolean>) => {
            state.singleInvoiceLoading = action.payload;
        },
        setMultipleInvoiceLoading: (state, action: PayloadAction<boolean>) => {
            state.multipleInvoiceLoading = action.payload;
        },
        setSelectedRecipients: (
            state,
            action: PayloadAction<IRecipientList>,
        ) => {
            state.recipients = action.payload;
        },
        resetSelectedRecipients: state => {
            state.recipients = initialState.recipients;
        },
        setFilteredDriverLoads: (
            state,
            action: PayloadAction<IDriverLoad[]>,
        ) => {
            state.filteredDriverLoads = action.payload;
        },
        setIsBOLRequired: (state, action: PayloadAction<boolean>) => {
            state.isBOLRequired = action.payload;
        },
        setIsScaleTicketRequired: (state, action: PayloadAction<boolean>) => {
            state.isScaleTicketRequired = action.payload;
        },
        setIsOtherRequired: (state, action: PayloadAction<boolean>) => {
            state.isOtherRequired = action.payload;
        },
        setIsQuickbooksRequired: (state, action: PayloadAction<boolean>) => {
            state.isQuickbooksRequired = action.payload;
        },
        setCustomerRecipients: (
            state,
            action: PayloadAction<{
                customerId: string;
                recipients: IRecipientList;
            }>,
        ) => {
            const { customerId, recipients } = action.payload;
            if (!state.customerStates[customerId]) {
                state.customerStates[customerId] = {
                    ...initialCustomerState,
                };
            }
            state.customerStates[customerId].recipients = recipients;
        },
        setCustomerIsBOLRequired: (
            state,
            action: PayloadAction<{ customerId: string; isRequired: boolean }>,
        ) => {
            const { customerId, isRequired } = action.payload;
            if (!state.customerStates[customerId]) {
                state.customerStates[customerId] = {
                    ...initialCustomerState,
                };
            }
            state.customerStates[customerId].isBOLRequired = isRequired;
        },
        setCustomerIsScaleTicketRequired: (
            state,
            action: PayloadAction<{ customerId: string; isRequired: boolean }>,
        ) => {
            const { customerId, isRequired } = action.payload;
            if (!state.customerStates[customerId]) {
                state.customerStates[customerId] = {
                    ...initialCustomerState,
                };
            }
            state.customerStates[customerId].isScaleTicketRequired = isRequired;
        },
        setCustomerIsOtherRequired: (
            state,
            action: PayloadAction<{ customerId: string; isRequired: boolean }>,
        ) => {
            const { customerId, isRequired } = action.payload;
            if (!state.customerStates[customerId]) {
                state.customerStates[customerId] = {
                    ...initialCustomerState,
                };
            }
            state.customerStates[customerId].isOtherRequired = isRequired;
        },
        setCustomerIsQuickbooksRequired: (
            state,
            action: PayloadAction<{ customerId: string; isRequired: boolean }>,
        ) => {
            const { customerId, isRequired } = action.payload;
            if (!state.customerStates[customerId]) {
                state.customerStates[customerId] = {
                    ...initialCustomerState,
                };
            }
            state.customerStates[customerId].isQuickbooksRequired = isRequired;
        },
        resetCustomerStates: state => {
            state.customerStates = {};
        },
        setSelectedCustomers: (state, action: PayloadAction<number[]>) => {
            state.selectedCustomers = action.payload;
        },
        resetSelectedCustomersList: state => {
            state.selectedCustomers = [];
        },
    },
});

export const {
    setInvoiceLoading,
    setSingleInvoiceLoading,
    setMultipleInvoiceLoading,
    setSelectedRecipients,
    setFilteredDriverLoads,
    setIsBOLRequired,
    setIsScaleTicketRequired,
    setIsOtherRequired,
    resetSelectedRecipients,
    setIsQuickbooksRequired,
    setCustomerRecipients,
    setCustomerIsBOLRequired,
    setCustomerIsScaleTicketRequired,
    setCustomerIsOtherRequired,
    setCustomerIsQuickbooksRequired,
    resetCustomerStates,
    setSelectedCustomers,
    resetSelectedCustomersList,
} = invoiceModal.actions;

const invoiceReducer = invoiceModal.reducer;
export default invoiceReducer;
